import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import ChefsSuggestion from "@/components/ChefsSuggestion";
import CommonStaples from "@/components/CommonStaples";
import { ShoppingBasket, Plus, Check, Trash2, ArrowLeftRight, Store, ShoppingBag, X as XIcon, Sparkles, Snowflake, Sun, RotateCw, CalendarClock, Search } from "lucide-react";
import { toast } from "sonner";
import { shelfLifeFor, daysRemaining } from "@/lib/shelfLife";
import PantryTips from "@/pages/grocery/PantryTips";
import AddedByAvatar from "@/pages/grocery/AddedByAvatar";
import OnlineShopIcon from "@/pages/grocery/OnlineShopIcon";
import ShopMode from "@/pages/grocery/ShopMode";
import VendorPickerChip from "@/pages/grocery/VendorPickerChip";
import useGroceryData from "@/hooks/useGroceryData";

const CATEGORIES = ["Produce", "Dairy", "Meat", "Bakery", "Pantry", "Staples", "Frozen", "Beverages", "Snacks", "Household", "Personal Care", "Other"];

export default function Grocery() {
  const {
    items, vendors, members, vendorSuggestions,
    tab, setTab, category, setCategory, search, setSearch,
    filtered, stapleMatches, onlineVendors,
    vendorName, memberFor,
    load,
  } = useGroceryData();

  const [open, setOpen] = useState(false);
  const [shopMode, setShopMode] = useState(false);
  const [form, setForm] = useState({ name: "", quantity: "", unit: "", category: "Produce", status: "upcoming", note: "", vendor_id: "" });
  const [frozenRefill, setFrozenRefill] = useState({ open: false, item: null, date: "", vendor_id: "" });
  const [vendorAutoPicked, setVendorAutoPicked] = useState(false); // did we just auto-pick a vendor?

  const addStapleToGrocery = async (s) => {
    try {
      // Auto-pick the vendor we've most often bought this staple from.
      // `vendorSuggestions` is learned from the user's own purchase
      // history via /api/grocery/vendor-suggestions, so it silently
      // "remembers" — no user config needed.
      const preferredVendorId =
        vendorSuggestions[(s.name || "").toLowerCase()]?.vendor_id || null;
      const { data: created } = await api.post("/grocery/quick-add", {
        name: s.name,
        category: "Staples",
        status: "upcoming",
        vendor_id: preferredVendorId,
      });
      const vendorName = preferredVendorId
        ? vendors.find((v) => v.id === preferredVendorId)?.name
        : null;
      toast.success(
        vendorName
          ? `Added ${s.name} to your list · will get from ${vendorName}`
          : `Added ${s.name} to your shopping list`
      );
      // Track for animation / debugging.
      void created;
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't add that.");
    }
  };

  const refillItem = async (item, expiryOverride, vendorOverride) => {
    try {
      const body = {};
      if (expiryOverride) body.expiry_date = expiryOverride;
      if (vendorOverride !== undefined) body.vendor_id = vendorOverride || null;
      await api.post(`/grocery/${item.id}/refill`, body);
      toast.success(`${item.name} refilled`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onRefillClick = (item) => {
    if (item.category === "Frozen") {
      const today = new Date();
      const in90 = new Date(today.getTime() + 90 * 24 * 3600 * 1000);
      // Pre-select the current or learned vendor as the default
      const suggested = vendorSuggestions[(item.name || "").toLowerCase()]?.vendor_id;
      setFrozenRefill({
        open: true,
        item,
        date: in90.toISOString().slice(0, 10),
        vendor_id: item.vendor_id || suggested || "",
      });
      return;
    }
    refillItem(item);
  };

  const confirmFrozenRefill = () => {
    if (!frozenRefill.item || !frozenRefill.date) return;
    refillItem(frozenRefill.item, frozenRefill.date, frozenRefill.vendor_id);
    setFrozenRefill({ open: false, item: null, date: "", vendor_id: "" });
  };

  // Online vendors + vendorName + memberFor now come from useGroceryData.

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      const payload = {
        ...form,
        status: tab === "pantry" ? "pantry" : "upcoming",
        vendor_id: form.vendor_id || null,
        unit: form.unit || null,
        // If moving straight to pantry, backfill purchase_date=today so expiry auto-calculates.
        purchase_date: tab === "pantry" ? new Date().toISOString().slice(0, 10) : null,
      };
      // /grocery/quick-add applies unit + expiry defaults on the server.
      await api.post("/grocery/quick-add", payload);
      setOpen(false);
      setForm({ name: "", quantity: "", unit: "", category: "Produce", status: "upcoming", note: "", vendor_id: "" });
      setVendorAutoPicked(false);
      toast.success("Added");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const move = async (id) => {
    await api.post(`/grocery/${id}/move`);
    toast.success(tab === "upcoming" ? "Moved to pantry" : "Moved to upcoming");
    load();
  };

  const remove = async (id) => {
    await api.delete(`/grocery/${id}`);
    load();
  };

  // ===== Shopping Mode (mobile-first, large checkboxes) =====
  if (shopMode) {
    return (
      <ShopMode
        items={items}
        vendors={vendors}
        categories={CATEGORIES}
        onExit={() => setShopMode(false)}
        onCheckOff={move}
      />
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Kitchen"
        title="Grocery"
        subtitle="What's running low, and what's already in the pantry."
        action={
          <div className="flex gap-2">
            <Button data-testid="grocery-shop-mode-button" onClick={() => setShopMode(true)} variant="ghost"
                    className="rounded-full border border-hearth-line hover:bg-hearth-stone h-10">
              <ShoppingBag size={15} className="mr-1.5" /> Shopping mode
            </Button>
            <Button data-testid="grocery-add-button" onClick={() => setOpen(true)}
              className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white px-5 h-10">
              <Plus size={15} className="mr-1.5" /> Add item
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-3 items-center mb-6">
        <div className="bg-hearth-paper border border-hearth-line rounded-full p-1 flex">
          {[["upcoming", "Upcoming buy"], ["pantry", "Pantry"]].map(([k, l]) => (
            <button
              key={k}
              data-testid={`grocery-tab-${k}`}
              onClick={() => setTab(k)}
              className={`px-5 py-2 text-sm rounded-full transition ${
                tab === k ? "bg-hearth-clay text-white" : "text-hearth-mute hover:text-hearth-ink"
              }`}
            >{l}</button>
          ))}
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[200px] rounded-full input-warm h-11" data-testid="grocery-category-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All categories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-hearth-mute" />
          <Input
            data-testid="grocery-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search items or presets…"
            className="input-warm h-11 rounded-full pl-9 pr-9"
          />
          {search && (
            <button
              data-testid="grocery-search-clear"
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-hearth-mute hover:text-hearth-ink hover:bg-hearth-stone"
              title="Clear search"
            >
              <XIcon size={12} />
            </button>
          )}
        </div>
      </div>

      {tab === "pantry" && <ChefsSuggestion onGroceryChanged={load} />}
      {tab === "upcoming" && <CommonStaples vendors={vendors} onAdded={load} search={search} />}

      {/* Cross-search matches from Staples & Spices — only when the user
          is actively searching. Lets them add a staple they already know
          about straight to the shopping list without hopping pages. */}
      {search.trim() && stapleMatches.length > 0 && (
        <div
          className="warm-card p-5 mb-4"
          data-testid="grocery-staples-matches"
        >
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
            <p className="caption inline-flex items-center gap-1.5">
              <Sparkles size={12} /> From your Staples &amp; Spices
            </p>
            <Link
              to="/staples"
              className="text-xs text-hearth-clayDark hover:underline"
              data-testid="grocery-staples-open"
            >
              Open Staples →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {stapleMatches.map(({ s, alreadyInList }) => (
              <div
                key={s.id}
                data-testid={`grocery-staple-match-${s.id}`}
                className="flex items-center justify-between gap-2 rounded-xl border border-hearth-line/70 px-3 py-2 bg-hearth-paper"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-hearth-ink truncate">{s.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-hearth-mute">
                    {s._bucket === "spice" ? "Spice" : (s.staple_type || "Staple")}
                    {typeof s.fill_level === "number" && (
                      <span className="ml-1.5">· {Math.round(s.fill_level * 100)}% full</span>
                    )}
                  </div>
                </div>
                {alreadyInList ? (
                  <span
                    className="chip bg-hearth-sage/15 text-hearth-sageDark shrink-0"
                    data-testid={`grocery-staple-inlist-${s.id}`}
                  >
                    On list
                  </span>
                ) : (
                  <button
                    onClick={() => addStapleToGrocery(s)}
                    data-testid={`grocery-staple-add-${s.id}`}
                    className="shrink-0 rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white h-8 px-3 text-xs inline-flex items-center gap-1"
                  >
                    <Plus size={12} /> To buy
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState icon={ShoppingBasket} title={tab === "upcoming" ? "Pantry's full." : "Nothing stored yet."}
                    message={tab === "upcoming" ? "Add things to buy next time you head out." : "Move bought items here from your shopping list."} />
      ) : (
        <div className="space-y-3">
          {(() => {
            // Defensive: any item whose category isn't in our canonical list
            // (e.g. an older seed row with a typo, or a future addition on
            // the backend) is bucketed under "Other" so it never silently
            // disappears from the tab.
            const known = new Set(CATEGORIES);
            const normalized = filtered.map((i) => ({
              ...i,
              _cat: known.has(i.category) ? i.category : "Other",
            }));
            return CATEGORIES
              .filter((c) => category === "All"
                ? normalized.some((i) => i._cat === c)
                : c === category)
              .map((cat) => {
                const group = normalized.filter((i) => i._cat === cat);
                if (!group.length) return null;
                return (
              <div key={cat} className="warm-card p-5">
                <div className="caption text-hearth-mute mb-3">{cat}</div>
                <div className="divide-y divide-hearth-line/70">
                  {group.map((it) => {
                    const remain = tab === "pantry" ? daysRemaining(it) : null;
                    const sl = tab !== "pantry" ? shelfLifeFor(it.name, it.category) : null;
                    const remainTone = (() => {
                      if (!remain) return "";
                      if (remain.remaining <= 1) return "bg-hearth-coral/15 text-hearth-coral";
                      if (remain.remaining <= 3) return "bg-hearth-amber/20 text-[#7A5320]";
                      return "bg-hearth-sage/15 text-hearth-sageDark";
                    })();
                    return (
                    <div key={it.id} className="flex items-center gap-3 py-3" data-testid={`grocery-row-${it.id}`}>
                      <button
                        onClick={() => move(it.id)}
                        data-testid={`grocery-move-${it.id}`}
                        className="w-7 h-7 shrink-0 rounded-full border-2 border-hearth-line hover:border-hearth-clay hover:bg-hearth-clay/10 grid place-items-center transition"
                        title={tab === "upcoming" ? "Mark as bought" : "Move back to list"}
                      >
                        {tab === "pantry" ? <Check size={14} className="text-hearth-sage" /> : <ArrowLeftRight size={12} className="text-hearth-mute" />}
                      </button>
                      <AddedByAvatar member={memberFor(it.created_by)} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-hearth-ink font-medium">{it.name}{it.quantity ? <span className="text-hearth-mute font-normal"> · {it.quantity}{it.unit ? ` ${it.unit}` : ""}</span> : null}</div>
                          {remain && (
                            <span className={`chip ${remainTone}`} data-testid={`grocery-shelf-${it.id}`}>
                              <Sparkles size={10} />
                              {remain.remaining === 0 ? "use today" : remain.remaining === 1 ? "1 day left" : `${remain.remaining} days left`}
                            </span>
                          )}
                          {!remain && sl && (
                            <span className="chip bg-hearth-sage/10 text-hearth-sageDark" data-testid={`grocery-shelf-${it.id}`}>
                              {sl.fridge > 0 ? <Snowflake size={10} /> : <Sun size={10} />}
                              lasts ~{Math.max(sl.fridge, sl.room)}d
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                          {it.note && <div className="text-xs text-hearth-mute">{it.note}</div>}
                          {(remain?.info?.tips || sl?.tips) && (
                            <div className="text-xs text-hearth-mute italic">{(remain?.info || sl).tips}</div>
                          )}
                          <VendorPickerChip
                            item={it}
                            vendors={vendors}
                            preferredVendorId={vendorSuggestions[(it.name || "").toLowerCase()]?.vendor_id || null}
                            onChanged={load}
                          />
                        </div>
                      </div>
                      {tab === "pantry" && (
                        <button
                          data-testid={`grocery-refill-${it.id}`}
                          onClick={() => onRefillClick(it)}
                          title={it.category === "Frozen" ? "Refill — pick new expiry" : "Bought again — extend expiry"}
                          className="p-2 rounded-full text-hearth-clayDark hover:text-hearth-clay hover:bg-hearth-clay/10"
                        >
                          {it.category === "Frozen" ? <CalendarClock size={15} /> : <RotateCw size={15} />}
                        </button>
                      )}
                      <OnlineShopIcon itemName={it.name} onlineVendors={onlineVendors} />
                      <button
                        data-testid={`grocery-delete-${it.id}`}
                        onClick={() => remove(it.id)}
                        className="p-2 rounded-full text-hearth-mute hover:text-hearth-coral hover:bg-hearth-coral/10"
                      ><Trash2 size={15} /></button>
                    </div>
                  );})}
                </div>
              </div>
            );
          });
          })()}
        </div>
      )}

      {tab === "pantry" && filtered.length > 0 && <PantryTips items={filtered} />}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-semibold">Add to grocery</DialogTitle>
            <DialogDescription className="text-hearth-mute">Add a new item to your list — we'll remember the vendor next time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="caption text-hearth-mute">Item</Label>
              <Input data-testid="grocery-form-name" className="input-warm mt-1.5 h-11"
                     value={form.name}
                     onChange={(e) => {
                       const next = { ...form, name: e.target.value };
                       // Vendor learning: if we've bought this item before, pre-pick
                       // that vendor. Only pre-picks — user can still change.
                       const key = e.target.value.trim().toLowerCase();
                       const suggested = key ? vendorSuggestions[key]?.vendor_id : null;
                       if (suggested && !form.vendor_id) {
                         next.vendor_id = suggested;
                         setVendorAutoPicked(true);
                       } else if (!suggested && vendorAutoPicked) {
                         // typed away from the known item — clear the auto pick
                         next.vendor_id = "";
                         setVendorAutoPicked(false);
                       }
                       setForm(next);
                     }}
                     placeholder="Tomatoes" />
            </div>
            <div className="grid grid-cols-6 gap-3">
              <div className="col-span-2">
                <Label className="caption text-hearth-mute">Quantity</Label>
                <Input data-testid="grocery-form-quantity" className="input-warm mt-1.5 h-11"
                       value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="2" />
              </div>
              <div className="col-span-2">
                <Label className="caption text-hearth-mute">Unit</Label>
                <Select value={form.unit || "auto"} onValueChange={(v) => setForm({ ...form, unit: v === "auto" ? "" : v })}>
                  <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl" data-testid="grocery-form-unit"><SelectValue placeholder="Auto" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (by category)</SelectItem>
                    {["each","g","kg","mL","L","pack","dozen","bunch","loaf","tin","bottle","jar"].map((u) => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="caption text-hearth-mute">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl" data-testid="grocery-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="caption text-hearth-mute">Note</Label>
              <Input data-testid="grocery-form-note" className="input-warm mt-1.5 h-11"
                     value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="organic if possible" />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <Label className="caption text-hearth-mute">Buy from (vendor)</Label>
                {vendorAutoPicked && form.vendor_id && (
                  <span
                    data-testid="grocery-vendor-learned-chip"
                    className="text-[10px] uppercase tracking-wider text-hearth-clayDark bg-hearth-clay/10 rounded-full px-2 py-0.5"
                    title="Suggested from your past purchases — feel free to change."
                  >
                    learned
                  </span>
                )}
              </div>
              <Select
                value={form.vendor_id || "none"}
                onValueChange={(v) => {
                  setForm({ ...form, vendor_id: v === "none" ? "" : v });
                  setVendorAutoPicked(false);
                }}
              >
                <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl" data-testid="grocery-form-vendor"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Any vendor</SelectItem>
                  {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}{v.type ? ` · ${v.type}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              {vendors.length === 0 && (
                <p className="text-xs text-hearth-mute mt-1.5">No vendors yet — add some at <Link to="/vendors" className="text-hearth-clay underline">/vendors</Link>.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="grocery-form-save" onClick={save}
                    className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Frozen refill: user picks a new expiry date manually */}
      <Dialog open={frozenRefill.open} onOpenChange={(o) => !o && setFrozenRefill({ open: false, item: null, date: "", vendor_id: "" })}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-semibold">
              Refill {frozenRefill.item?.name || "item"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-hearth-mute">
              Frozen items don&apos;t follow a standard shelf-life — pick the expiry date that suits this batch.
            </p>
            <div>
              <Label className="caption text-hearth-mute">New expiry date</Label>
              <Input
                type="date"
                data-testid="grocery-refill-frozen-date"
                className="input-warm mt-1.5 h-11"
                value={frozenRefill.date}
                onChange={(e) => setFrozenRefill({ ...frozenRefill, date: e.target.value })}
              />
            </div>
            {vendors.length > 0 && (
              <div>
                <div className="flex items-baseline justify-between">
                  <Label className="caption text-hearth-mute">Bought from</Label>
                  {frozenRefill.item && !frozenRefill.item.vendor_id &&
                    vendorSuggestions[(frozenRefill.item.name || "").toLowerCase()]?.vendor_id === frozenRefill.vendor_id &&
                    frozenRefill.vendor_id && (
                      <span className="text-[10px] uppercase tracking-wider text-hearth-clayDark bg-hearth-clay/10 rounded-full px-2 py-0.5">
                        learned
                      </span>
                  )}
                </div>
                <Select
                  value={frozenRefill.vendor_id || "none"}
                  onValueChange={(v) => setFrozenRefill({ ...frozenRefill, vendor_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl" data-testid="grocery-refill-frozen-vendor">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Any vendor</SelectItem>
                    {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}{v.type ? ` · ${v.type}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFrozenRefill({ open: false, item: null, date: "", vendor_id: "" })} className="rounded-full">Cancel</Button>
            <Button
              data-testid="grocery-refill-frozen-confirm"
              onClick={confirmFrozenRefill}
              disabled={!frozenRefill.date}
              className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white"
            >Refill</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

